<template>
  <div>
    <div class="flex items-center mb-5">
      <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
        Invoices / Credits
      </h3>
      <hr class="flex-1 border-primary mx-4" />
    </div>

    <div v-if="items.length > 0" class="overflow-y-auto" style="max-height: 65vh;">
      <table class="w-full text-sm border-separate" style="border-spacing: 0;">
        <thead>
          <tr class="border-b-2 border-primary text-left">
            <th class="py-2 pr-6 sticky top-0 bg-white">Type</th>
            <th class="py-2 pr-6 sticky top-0 bg-white">Number</th>
            <th class="py-2 pr-6 whitespace-nowrap sticky top-0 bg-white">Date</th>
            <th class="py-2 pr-6 sticky top-0 bg-white">Description</th>
            <th class="py-2 pr-6 text-right whitespace-nowrap sticky top-0 bg-white">Amount</th>
            <th class="py-2 pr-6 whitespace-nowrap sticky top-0 bg-white">Status</th>
            <th class="py-2 pl-2 whitespace-nowrap sticky top-0 bg-white">Expiry</th>
          </tr>
        </thead>
        <tbody>
          <tr
              v-for="(item, index) in items"
              :key="item.type + item.number + item.date + index"
              class="border-b border-gray-200"
              :class="{ 'hover:bg-gray-100 cursor-pointer': item.clickable }"
              @click="openItem(item)"
          >
            <td class="py-2.5 pr-6">{{ item.type }}</td>
            <td class="py-2.5 pr-6">{{ item.number }}</td>
            <td class="py-2.5 pr-6 whitespace-nowrap">{{ formatDate(item.date) }}</td>
            <td class="py-2.5 pr-6">{{ item.description }}</td>
            <td class="py-2.5 pr-6 text-right whitespace-nowrap">{{ formatMoney(item.amount) }}</td>
            <td class="py-2.5 pr-6 uppercase whitespace-nowrap">{{ item.status }}</td>
            <td class="py-2.5 pl-2 whitespace-nowrap" :class="{ 'text-red-600 font-semibold': isExpired(item.expiry) }">{{ formatExpiry(item.expiry) }}</td>
          </tr>
        </tbody>
      </table>
    </div>
    <p class="mx-auto my-16 text-center" v-if="!isLoading && items.length === 0">
      There are no invoices or credits for this contact.
    </p>
  </div>
</template>

<script>
export default {
  name: "InvoicesCredits",
  props: {
    contactId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      items: [],
      isLoading: false,
    };
  },
  async mounted() {
    await this.loadItems();
  },
  methods: {
    async loadItems() {
      this.isLoading = true;
      try {
        const params = `sold_to=${this.contactId}&daysAhead=36500&limit=200`;

        const [invoices, credits] = await Promise.all([
          this.loadAllPages((page) => this.InvoiceService.searchAllInvoices(`?${params}&page=${page}`)),
          this.loadAllPages((page) => this.CreditService.searchAllCredits(`?${params}&page=${page}`)),
        ]);

        const invoiceEntries = invoices.map((invoice) => ({
          type: invoice.is_pro_forma ? "Invoice (Pro-Forma)" : "Invoice",
          number: invoice.invoice_no,
          date: invoice.invoice_date,
          time: invoice.created_at || invoice.invoice_date,
          description: "Invoice raised",
          amount: invoice.total_value,
          status: invoice.status,
          clickable: true,
          route: { name: "invoice-edit", params: { invoice_id: invoice.invoice_id } },
        }));

        const creditEntries = [];
        credits.forEach((credit) => {
          // The credit itself: the full amount placed on the account when it was created/issued
          creditEntries.push({
            type: credit.credit_source === "invoice_correction" ? "Credit Note" : "Open Credit",
            number: credit.credit_no,
            date: credit.credit_date,
            time: credit.created_at || credit.credit_date,
            description: credit.credit_source === "invoice_correction"
                ? `Credit note for ${credit.invoice_no || "invoice"}`
                : "Credit added to account",
            amount: Math.abs(credit.total_value || 0),
            status: credit.status,
            expiry: credit.credit_source === "invoice_correction" ? null : credit.credit_expiry_date,
            clickable: true,
            route: { name: "credit-edit", params: { credit_id: credit.credit_id } },
          });

          // Each time this credit was drawn down against an invoice is its own transaction -
          // an Open Credit being applied to pay down an invoice is a different event from a
          // Credit Note being issued/settled against the invoice it was created for
          (credit.applications || []).forEach((application) => {
            creditEntries.push({
              type: credit.credit_source === "invoice_correction" ? "Credit Note Issued" : "Open Credit Applied",
              number: credit.credit_no,
              date: application.applied_at,
              time: application.applied_at,
              description: `Applied to ${application.invoice_no}`,
              amount: -application.amount,
              status: "",
              clickable: true,
              route: { name: "invoice-edit", params: { invoice_id: application.invoice_id } },
            });
          });

          // An issued credit note that was later voided put its amount back onto the invoice.
          // Without this row the history would still read as if the money had come off.
          if (credit.status === "void" && credit.voided_at && (credit.voided_amount || 0) > 0 && credit.invoice_id) {
            creditEntries.push({
              type: "Credit Note Voided",
              number: credit.credit_no,
              date: credit.voided_at,
              time: credit.voided_at,
              description: `${this.formatMoney(credit.voided_amount)} put back on ${credit.invoice_no}`,
              amount: credit.voided_amount,
              status: "",
              clickable: true,
              route: { name: "invoice-edit", params: { invoice_id: credit.invoice_id } },
            });
          }
        });

        // Newest first: by the day shown in the Date column, then by the exact time within
        // that day so entries made on the same day keep the order they happened in
        const dayOf = (unix) => (unix ? this.$moment.unix(unix).startOf("day").unix() : 0);
        this.items = [...invoiceEntries, ...creditEntries]
            .sort((a, b) => (dayOf(b.date) - dayOf(a.date)) || ((b.time || 0) - (a.time || 0)));
      } catch (error) {
        console.error("Error loading invoices/credits for contact:", error);
      } finally {
        this.isLoading = false;
      }
    },
    // The list endpoints page their results, so keep asking for the next page until the
    // customer's whole history is in. A long-standing account can easily pass one page.
    async loadAllPages(fetchPage) {
      const rows = [];
      let page = 1;
      let pageCount = 1;
      do {
        const result = await fetchPage(page);
        rows.push(...(result.data || []));
        pageCount = result.page_count || 1;
        page++;
      } while (page <= pageCount);
      return rows;
    },
    openItem(item) {
      if (!item.clickable || !item.route) {
        return;
      }
      this.$router.push(item.route);
    },
    formatDate(unixTimestamp) {
      if (!unixTimestamp) {
        return "";
      }
      return this.$moment.unix(unixTimestamp).format("DD-MM-YYYY");
    },
    formatMoney(value) {
      const amount = parseFloat(value) || 0;
      const formatted = Math.abs(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return amount < 0 ? `- £${formatted}` : `£${formatted}`;
    },
    isExpired(expiry) {
      if (!expiry) {
        return false;
      }
      return this.$moment.unix(expiry).endOf('day').isBefore(this.$moment());
    },
    formatExpiry(expiry) {
      if (!expiry) {
        return "";
      }
      if (this.isExpired(expiry)) {
        return "Expired";
      }
      const daysRemaining = this.$moment.unix(expiry).endOf('day').diff(this.$moment(), 'days');
      if (daysRemaining <= 0) {
        return "Expires today";
      }
      return `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`;
    },
  },
};
</script>
